import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import type { EventMember, SfdcUser } from '../../event-manager-client';
import { isClayHost, getAccountBadge } from './utils';
import { MemberCard } from './MemberCard';

interface UnassignedPanelProps {
  members: EventMember[];
  sfdcUsers: SfdcUser[];
  onDrop: (memberId: number) => void;
  onSelectMember: (member: EventMember) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export function UnassignedPanel({ members, sfdcUsers, onDrop, onSelectMember, collapsed, onToggleCollapse }: UnassignedPanelProps) {
  const [over, setOver] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const statuses = useMemo(() => {
    const set = new Set(members.map(m => m.cm_status).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [members]);

  const filtered = useMemo(() => {
    let result = members;
    if (statusFilter) {
      result = result.filter(m => m.cm_status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(m =>
        m.contact_name?.toLowerCase().includes(q) ||
        m.account_name?.toLowerCase().includes(q) ||
        m.contact_email?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [members, statusFilter, searchQuery]);

  const { clayHosts, guests } = useMemo(() => {
    const hosts: EventMember[] = [];
    const rest: EventMember[] = [];
    for (const m of filtered) {
      if (isClayHost(m, sfdcUsers)) hosts.push(m);
      else rest.push(m);
    }
    return { clayHosts: hosts, guests: rest };
  }, [filtered, sfdcUsers]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setOver(false);
    const memberId = Number(e.dataTransfer.getData('text/plain'));
    if (!isNaN(memberId)) onDrop(memberId);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragEnter={() => setOver(true)}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
      }}
      onDrop={handleDrop}
      className={`flex flex-col h-full rounded-xl border bg-white dark:bg-nightshade-900 transition-all duration-300 overflow-hidden ${
        collapsed ? 'w-0 border-transparent' : 'w-[280px]'
      } ${
        over
          ? 'border-blueberry-400 dark:border-blueberry-500'
          : collapsed ? '' : 'border-nightshade-200 dark:border-nightshade-700'
      }`}
    >
      <div className="w-[280px] flex flex-col h-full flex-shrink-0">
        {/* Header */}
        <div className="px-4 py-3 border-b border-nightshade-100 dark:border-nightshade-800">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-[10px] uppercase tracking-widest font-bold text-nightshade-400 dark:text-nightshade-500">
              Unassigned
            </h3>
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-nightshade-100 dark:bg-nightshade-800 text-nightshade-600 dark:text-nightshade-400">
              {filtered.length}
            </span>
            <button
              onClick={onToggleCollapse}
              className="ml-auto p-1 rounded hover:bg-nightshade-100 dark:hover:bg-nightshade-800 text-nightshade-400 hover:text-nightshade-600 transition-colors"
              title="Collapse panel"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-nightshade-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search members..."
              className="w-full text-xs bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-full pl-6 pr-2 py-1 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-1 focus:ring-blueberry-400"
            />
          </div>
          {statuses.length > 1 && (
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="w-full text-xs bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-md px-2 py-1 text-nightshade-700 dark:text-nightshade-300 focus:outline-none focus:ring-1 focus:ring-blueberry-400 mt-1.5"
            >
              <option value="">All statuses</option>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>

        {/* Member list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {filtered.length === 0 ? (
            <p className="text-xs text-nightshade-400 dark:text-nightshade-500 text-center py-6 italic">
              {members.length === 0 ? 'All members assigned' : 'No matches'}
            </p>
          ) : (
            <>
              {clayHosts.length > 0 && (
                <>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-blueberry-500 px-1 pt-1">
                    Clay Hosts
                  </p>
                  {clayHosts.map(m => (
                    <MemberCard
                      key={m.id}
                      member={m}
                      onClick={() => onSelectMember(m)}
                      isClayHost
                      accountBadge={getAccountBadge(m.account_type)}
                    />
                  ))}
                </>
              )}
              {guests.length > 0 && (
                <>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-nightshade-400 dark:text-nightshade-500 px-1 pt-2">
                    Guests
                  </p>
                  {guests.map(m => (
                    <MemberCard
                      key={m.id}
                      member={m}
                      onClick={() => onSelectMember(m)}
                      accountBadge={getAccountBadge(m.account_type)}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
